import { motion } from "framer-motion";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Roteiros from "@/components/Roteiros";
import { KineticLine, FadeUp } from "@/components/KineticText";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const HISTORY_IMG =
  "https://images.unsplash.com/photo-1554254648-2d58a1bc3fd5?q=80&w=1400&auto=format&fit=crop";

const VALUES = [
  { title: "Atendimento próximo", text: "Do primeiro olá ao desembarque, com alguém do nosso lado sempre a uma mensagem de distância." },
  { title: "Roteiros sob medida", text: "Cada viagem é montada do seu jeito: cabine, navio, datas e passeios em terra." },
  { title: "Amor pelo mar", text: "Viajamos de navio também — e colocamos essa experiência em cada detalhe da sua travessia." },
];

const TEASERS = [
  {
    src: "https://images.unsplash.com/photo-1562672753-8cccbe9806ce?q=80&w=1200&auto=format&fit=crop",
    alt: "Piscina do convés de um navio de cruzeiro",
  },
  {
    src: "https://images.unsplash.com/photo-1599700548547-10a85af5b390?q=80&w=1200&auto=format&fit=crop",
    alt: "Navio de cruzeiro branco navegando em mar azul",
  },
  {
    src: "https://images.unsplash.com/photo-1585793753011-397e6e4668d6?q=80&w=1200&auto=format&fit=crop",
    alt: "Navio de cruzeiro ao pôr do sol",
  },
];

export default function Home() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <Hero />

      {/* Nossa História */}
      <section data-testid="history-section" className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-40">
        <div className="grid gap-16 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-6 lg:col-span-5">
            <FadeUp>
              <p className="text-xs font-medium uppercase tracking-[0.35em] text-dusk">Nossa História</p>
            </FadeUp>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.02] text-navy md:text-6xl">
              <KineticLine>Nascemos do mar,</KineticLine>
              <KineticLine delay={0.12}>
                <em className="italic text-sea">para o mar.</em>
              </KineticLine>
            </h2>

            <FadeUp delay={0.15} className="mt-10 space-y-6 text-base font-light leading-relaxed text-dusk md:text-lg">
              <p>
                A SG Turismos nasceu de uma sensação simples: estar no convés, o vento
                na cara e o horizonte se abrindo ali na frente. Quem já viajou de navio
                conhece — e quem ainda não conheceu, a gente tem pressa em mostrar.
              </p>
              <p>
                Desde então, cuidamos de cada detalhe da travessia: da escolha da
                cabine ao embarque, do jantar no deck ao passeio em terra. Você aproveita
                o caminho; a gente cuida do resto.
              </p>
            </FadeUp>

            <div className="mt-14 space-y-0">
              {VALUES.map((v, i) => (
                <FadeUp key={v.title} delay={0.1 + i * 0.08}>
                  <div data-testid={`history-value-${i}`} className="group border-t border-cloud py-6 transition-colors duration-300 hover:border-sea">
                    <div className="flex items-baseline justify-between gap-6">
                      <h3 className="font-serif text-xl text-navy md:text-2xl">{v.title}</h3>
                      <span className="font-serif text-sm italic text-sea">0{i + 1}</span>
                    </div>
                    <p className="mt-2 max-w-sm text-sm font-light leading-relaxed text-dusk">{v.text}</p>
                  </div>
                </FadeUp>
              ))}
              <div className="border-t border-cloud" />
            </div>
          </div>

          <div className="md:col-span-6 lg:col-span-7 lg:pl-10">
            <FadeUp delay={0.2} y={60} className="relative">
              <div className="absolute -left-4 -top-4 hidden h-full w-full border border-cloud md:block" aria-hidden="true" />
              <div className="relative overflow-hidden">
                <motion.img
                  src={HISTORY_IMG}
                  alt="Navio de cruzeiro navegando durante o pôr do sol"
                  className="h-[380px] w-full object-cover md:h-[640px]"
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                />
                <p className="absolute bottom-0 left-0 bg-paper/90 px-4 py-2 font-serif text-sm italic text-navy backdrop-blur">
                  O caminho é o destino.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee />

      <Roteiros />

      {/* Teaser do álbum */}
      <section data-testid="album-teaser" className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-40">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <FadeUp>
              <p className="text-xs font-medium uppercase tracking-[0.35em] text-dusk">Álbum</p>
            </FadeUp>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-navy md:text-6xl">
              <KineticLine>Destinos que cabem</KineticLine>
              <KineticLine delay={0.12}>
                <em className="italic text-sea">num olhar.</em>
              </KineticLine>
            </h2>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/album"
              data-testid="album-teaser-link"
              className="group inline-flex items-center gap-3 border-b border-navy pb-2 text-[0.7rem] font-semibold uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:text-sea hover:border-sea"
            >
              Ver álbum completo
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeUp>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-3 md:gap-6">
          {TEASERS.map((t, i) => (
            <FadeUp key={t.src} delay={i * 0.1} y={48}>
              <Link to="/album" data-testid={`album-teaser-photo-${i}`} className="group block overflow-hidden">
                <img
                  src={t.src}
                  alt={t.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05] ${
                    i === 1 ? "aspect-[4/5] sm:aspect-[3/4]" : "aspect-[4/5]"
                  }`}
                />
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </motion.main>
  );
}
